import { Document, View, Image, Page, Text } from '@react-pdf/renderer';
import classNames from 'classnames';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import { useBeneficiaryContext } from 'context/beneficiaryContext';
import React from 'react';
import { Card, Col, Row, Table } from 'react-bootstrap';
import sponsorCard from '../../../assets/img/sponsor-card.jpg';
// import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

export const OrphanDonationCard = ({ beneficiary }) => {
  // Helper to safely format dates
  const formatDate = (date) => {
    if (!date) return '-';
    try {
      return format(new Date(date), 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  // Build full name
  const fullName = [
    beneficiary?.first_name,
    beneficiary?.middle_name,
    beneficiary?.last_name
  ].filter(Boolean).join(' ');

  // Father and mother come from the parents table. The guardian (often a
  // centre or relative) is not the father, so it never goes in that box.
  const personName = p =>
    [p?.first_name, p?.middle_name, p?.last_name]
      .filter(Boolean)
      .map(s => String(s).trim())
      .filter(Boolean)
      .join(' ');
  const parents = beneficiary?.parents || [];
  const father = parents.find(p => p.parent_type === 'father');
  const mother = parents.find(p => p.parent_type === 'mother');
  // Older records only have the status on the guardian row, either as text
  // or as an id into a lookup table (resolved by getBeneficiary)
  const legacyStatus = (raw, lookup) =>
    lookup?.name ||
    (raw && !/^[0-9a-f-]{36}$/i.test(String(raw)) ? String(raw) : '');
  const fatherStatus =
    father?.status ||
    legacyStatus(
      beneficiary?.guardian?.father_status,
      beneficiary?.guardian?.guardian_father_status
    );
  const motherStatus =
    mother?.status ||
    legacyStatus(
      beneficiary?.guardian?.mother_status,
      beneficiary?.guardian?.guardian_mother_status
    );

  // Get health condition
  const healthCondition = beneficiary?.sponsorship_condition?.condition || beneficiary?.health_notes || '';

  return (
    <Document>
      <Page orientation="landscape">
        <View style={{ width: '100%', position: 'absolute' }}>
          <Image src={sponsorCard} cache={false} />
          {beneficiary?.image && (
            <Image
              src={beneficiary.image}
              style={{
                height: '138px',
                width: '144px',
                position: 'absolute',
                top: '40px',
                left: '65px'
              }}
            />
          )}

          {/* Full Name */}
          <Text
            style={{
              position: 'absolute',
              top: '57px',
              left: '230px',
              fontSize: 12
            }}
          >
            {fullName}
          </Text>

          {/* Beneficiary ID */}
          <Text
            style={{
              position: 'absolute',
              top: '110px',
              left: '230px',
              fontSize: 12
            }}
          >
            {beneficiary?.simple_id ? `B${beneficiary.simple_id}` : ''}
          </Text>

          {/* Country */}
          <Text
            style={{
              position: 'absolute',
              top: '156px',
              left: '230px',
              fontSize: 12
            }}
          >
            {beneficiary?.country || ''}
          </Text>

          {/* Date of Birth */}
          <Text
            style={{
              position: 'absolute',
              top: '230px',
              left: '65px',
              fontSize: 12
            }}
          >
            {formatDate(beneficiary?.DOB)}
          </Text>

          {/* Gender */}
          <Text
            style={{
              position: 'absolute',
              top: '230px',
              left: '470px',
              fontSize: 12
            }}
          >
            {beneficiary?.gender || ''}
          </Text>

          {/* Health Condition (box ends where Interests/Hobbies starts) */}
          <Text
            style={{
              position: 'absolute',
              top: '284px',
              left: '65px',
              maxWidth: '170px',
              fontSize: 12
            }}
          >
            {healthCondition}
          </Text>

          {/* Interests / Hobbies */}
          <Text
            style={{
              position: 'absolute',
              top: '284px',
              left: '245px',
              maxWidth: '195px',
              fontSize: 12
            }}
          >
            {beneficiary?.hobbies || ''}
          </Text>

          {/* Language */}
          <Text
            style={{
              position: 'absolute',
              top: '284px',
              left: '460px',
              fontSize: 12
            }}
          >
            {beneficiary?.language || ''}
          </Text>

          {/* Inspiration / Career Aspiration */}
          <Text
            style={{
              position: 'absolute',
              top: '335px',
              left: '65px',
              fontSize: 12
            }}
          >
            {beneficiary?.inspiration || ''}
          </Text>

          {/* Father's Name */}
          <Text
            style={{
              position: 'absolute',
              top: '383px',
              left: '65px',
              fontSize: 12
            }}
          >
            {personName(father)}
          </Text>

          {/* Father's Status */}
          <Text
            style={{
              position: 'absolute',
              top: '383px',
              left: '460px',
              fontSize: 12
            }}
          >
            {fatherStatus}
          </Text>

          {/* Mother's Name */}
          <Text
            style={{
              position: 'absolute',
              top: '430px',
              left: '65px',
              fontSize: 12
            }}
          >
            {personName(mother)}
          </Text>

          {/* Mother's Status */}
          <Text
            style={{
              position: 'absolute',
              top: '430px',
              left: '460px',
              fontSize: 12
            }}
          >
            {motherStatus}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
OrphanDonationCard.propTypes = {
  beneficiary: PropTypes.object
};

const ProfileBeneficiaryInfo = () => {
  const { beneficiary } = useBeneficiaryContext();

  const profileDetails = [
    {
      id: 1,
      title: 'Name',
      value:
        (beneficiary.guardian?.first_name || '') +
        ' ' +
        (beneficiary.guardian?.middle_name || '') +
        ' ' +
        (beneficiary.guardian?.last_name || '')
    },
    {
      id: 2,
      title: 'Relationship',
      value: beneficiary.guardian?.guardian_relationShip?.name
    },
    { id: 3, title: 'Contact Number', value: beneficiary.guardian?.phone },
    { id: 4, title: 'Email', value: beneficiary.guardian?.email },
    { id: 5, title: 'Address', value: beneficiary.guardian?.address },
    { id: 6, title: 'Occupation', value: beneficiary.guardian?.occupation },
    { id: 7, title: 'Monthly Income', value: beneficiary.guardian?.monthly_income_range },
    { id: 8, title: 'Notes', value: beneficiary.guardian?.notes }
  ];
  return (
    <>
      <Card className="mb-3">
        <FalconCardHeader
          title={<h4 className="fw-bold">More information</h4>}
          light={false}
          titleTag="div"
          className="pb-1"
          menuClassName="align-items-start"
        />
        <Card.Body className="pb-0">
          <Row>
            <Col>
              <Flex>
                <div className="donor-info-title">
                  <p className="mb-2 text-900" style={{ fontSize: '1rem' }}>Father's Status</p>
                  <p className="mb-0 text-900" style={{ fontSize: '1rem' }}>Mother's Status</p>
                </div>
                <div>
                  <p className="mb-2">
                    {beneficiary.guardian?.guardian_father_status?.name || '-'}
                  </p>
                  <p className="mb-2">
                    {beneficiary.guardian?.guardian_mother_status?.name || '-'}
                  </p>
                </div>
              </Flex>
              <hr />
            </Col>
          </Row>
        </Card.Body>
        <FalconCardHeader
          title={<h4 className="fw-bold">Guardian Details</h4>}
          light={false}
          titleTag="div"
          className="pb-1"
          menuClassName="align-items-start"
        />

        <Card.Body>
          <Row>
            <Col md={8}>
              <Table size="sm" borderless className="font-sans-serif mb-0">
                <tbody>
                  {profileDetails?.map(detail => (
                    <tr key={detail.id}>
                      <td className="text-start ps-0 text-900" style={{ fontSize: '1rem' }}>{detail.title}</td>
                      <td
                        className={classNames(
                          'text-start',
                          detail.valueClassName
                        )}
                      >
                        {detail.link ? (
                          <a href={detail.link}>{detail.value}</a>
                        ) : (
                          detail.value || '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </>
  );
};

export default ProfileBeneficiaryInfo;
